import { useState } from "react";
import { useCouriers, CourierProvider } from "../../../context/CourierContext";
import {
  Box, Typography, Chip, IconButton, Paper,
  Accordion, AccordionSummary, AccordionDetails,
  CircularProgress,
} from "@mui/material";
import Button from "@mui/material/Button";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FlightIcon from "@mui/icons-material/Flight";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import BoltIcon from "@mui/icons-material/Bolt";
import PlaceIcon from "@mui/icons-material/Place";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SlabTable from "../../../components/courier/SlabTable";
import CourierDialog from "../../../components/courier/CourierDialog";
import ServiceDialog from "../../../components/courier/ServiceDialog";
import RateCardDialog from "../../../components/courier/RateCardDialog";
import DeleteConfirm from "../../../components/courier/DeleteConfirm";

const modeIcon = {
  Air: <FlightIcon sx={{ fontSize: 16 }} />,
  Surface: <DirectionsBoatIcon sx={{ fontSize: 16 }} />,
  Express: <BoltIcon sx={{ fontSize: 16 }} />,
};

const CourierList = () => {
  const ctx = useCouriers();
  const { loading } = ctx;

  console.log(ctx.couriers, "ctxcouriers");

  const [courierDialog, setCourierDialog] = useState({ open: false });
  const [courierDelete, setCourierDelete] = useState(null);
  const [serviceDialog, setServiceDialog] = useState({ open: false, courierId: "" });
  const [serviceDelete, setServiceDelete] = useState(null);
  const [rcDialog, setRcDialog] = useState({ open: false, courierId: "", serviceId: "" });
  const [rcDelete, setRcDelete] = useState(null);

  const stopProp = (e) => e.stopPropagation();

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Box sx={{ maxWidth: 1200, mx: "auto", px: 3, py: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Box>
            <Typography variant="h5">Courier Master</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage couriers, services, rate cards and weight slabs
            </Typography>
          </Box>
          <Button variant="contained"  startIcon={<AddIcon />} onClick={() => setCourierDialog({ open: true })}>
            Add Courier
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && ctx.couriers.length === 0 && (
          <Paper sx={{ p: 6, textAlign: "center", color: "text.secondary" }}>
            <LocalShippingIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
            <Typography>No couriers yet. Click "Add Courier" to get started.</Typography>
          </Paper>
        )}

        {ctx.couriers.map((courier) => (
          <Accordion key={courier.id} sx={{ mb: 1.5, borderRadius: 2, "&:before": { display: "none" } }} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: "primary.main", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <LocalShippingIcon sx={{ color: "#fff", fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography fontWeight={600}>{courier.name}</Typography>
                    <Chip label={courier.status} size="small" color={courier.status === "Active" ? "success" : "default"} sx={{ height: 20, fontSize: 11 }} />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Code: {courier.code} · {courier.contactPerson} · {courier.phone}
                  </Typography>
                </Box>
                <Box onClick={stopProp} sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton size="small" onClick={() => setCourierDialog({ open: true, edit: courier })}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => setCourierDelete(courier)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pl: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Services ({courier.services.length})
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setServiceDialog({ open: true, courierId: courier.id })}>
                  Add Service
                </Button>
              </Box>

              {courier.services.map((service) => (
                <Accordion key={service.id} sx={{ mb: 1, bgcolor: "action.hover", "&:before": { display: "none" } }} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                      {modeIcon[service.mode]}
                      <Typography variant="body2" fontWeight={500}>{service.name}</Typography>
                      <Typography variant="caption" color="text.secondary">({service.code})</Typography>
                      <Chip label={service.mode} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                      <Typography variant="caption" color="text.secondary" sx={{ ml: "auto", mr: 1 }}>{service.deliveryDays}</Typography>
                      <Box onClick={stopProp} sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton size="small" onClick={() => setServiceDialog({ open: true, courierId: courier.id, edit: service })}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setServiceDelete({ courierId: courier.id, service })}>
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pl: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Rate Cards ({service.rateCards.length})
                      </Typography>
                      <Button size="small" startIcon={<AddIcon />} onClick={() => setRcDialog({ open: true, courierId: courier.id, serviceId: service.id })}>
                        Add Rate Card
                      </Button>
                    </Box>

                    {service.rateCards.map((rc) => (
                      <Accordion key={rc.id} sx={{ mb: 1, bgcolor: "background.paper", "&:before": { display: "none" } }} disableGutters>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                            <Typography variant="body2" fontWeight={500}>{rc.name}</Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto", mr: 1 }}>
                              <PlaceIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">{rc.zone}</Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 1 }}>
                              <CalendarTodayIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">{rc.effectiveFrom}</Typography>
                            </Box>
                            <Box onClick={stopProp} sx={{ display: "flex", gap: 0.5 }}>
                              <IconButton size="small" onClick={() => setRcDialog({ open: true, courierId: courier.id, serviceId: service.id, edit: rc })}>
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => setRcDelete({ courierId: courier.id, serviceId: service.id, rc })}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <SlabTable
                            slabs={rc.slabs}
                            onAdd={(sl) => ctx.addSlab(courier.id, service.id, rc.id, sl)}
                            onUpdate={(slId, sl) => ctx.updateSlab(courier.id, service.id, rc.id, slId, sl)}
                            onDelete={(slId) => ctx.deleteSlab(courier.id, service.id, rc.id, slId)}
                          />
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <CourierDialog open={courierDialog.open} onOpenChange={(o) => setCourierDialog({ open: o })} title={courierDialog.edit ? "Edit Courier" : "Add Courier"}
        initial={courierDialog.edit ? { name: courierDialog.edit.name, code: courierDialog.edit.code, contactPerson: courierDialog.edit.contactPerson, phone: courierDialog.edit.phone, status: courierDialog.edit.status } : undefined}
        onSubmit={async (data) => courierDialog.edit ? await ctx.updateCourier(courierDialog.edit.id, data) : await ctx.addCourier(data)} />
      <DeleteConfirm open={!!courierDelete} onOpenChange={(o) => !o && setCourierDelete(null)}
        onConfirm={async () => { if (courierDelete) await ctx.deleteCourier(courierDelete.id); setCourierDelete(null); }} itemName={courierDelete?.name || ""} />

      <ServiceDialog open={serviceDialog.open} onOpenChange={(o) => setServiceDialog((p) => ({ ...p, open: o }))} title={serviceDialog.edit ? "Edit Service" : "Add Service"}
        initial={serviceDialog.edit ? { name: serviceDialog.edit.name, code: serviceDialog.edit.code, deliveryDays: serviceDialog.edit.deliveryDays, mode: serviceDialog.edit.mode } : undefined}
        onSubmit={async (data) => serviceDialog.edit ? await ctx.updateService(serviceDialog.courierId, serviceDialog.edit.id, data) : await ctx.addService(serviceDialog.courierId, data)} />
      <DeleteConfirm open={!!serviceDelete} onOpenChange={(o) => !o && setServiceDelete(null)}
        onConfirm={async () => { if (serviceDelete) await ctx.deleteService(serviceDelete.courierId, serviceDelete.service.id); setServiceDelete(null); }} itemName={serviceDelete?.service.name || ""} />

      <RateCardDialog open={rcDialog.open} onOpenChange={(o) => setRcDialog((p) => ({ ...p, open: o }))} title={rcDialog.edit ? "Edit Rate Card" : "Add Rate Card"}
        initial={rcDialog.edit ? { name: rcDialog.edit.name, zone: rcDialog.edit.zone, effectiveFrom: rcDialog.edit.effectiveFrom } : undefined}
        onSubmit={(data) => rcDialog.edit ? ctx.updateRateCard(rcDialog.courierId, rcDialog.serviceId, rcDialog.edit.id, data) : ctx.addRateCard(rcDialog.courierId, rcDialog.serviceId, data)} />
      <DeleteConfirm open={!!rcDelete} onOpenChange={(o) => !o && setRcDelete(null)}
        onConfirm={() => { if (rcDelete) ctx.deleteRateCard(rcDelete.courierId, rcDelete.serviceId, rcDelete.rc.id); setRcDelete(null); }} itemName={rcDelete?.rc.name || ""} />
    </Box>
  );
};

const CourierListWithProvider = () => (
  <CourierProvider>
    <CourierList />
  </CourierProvider>
);

export default CourierListWithProvider;
